import { useState } from "react";
import { useNavigate } from "react-router-dom";
import welcomeBotanical from "@/assets/welcome-botanical.jpg";

export default function WelcomeSplash() {
  /**
   * USER JOURNEY: Welcome Splash Screen
   * Shown once to first-time visitors. Flow: /splash → /
   */
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);

  const handleContinue = () => {
    localStorage.setItem("aura-splash-seen", "true");
    navigate("/");
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#F9EAE0] via-[#FFF7F0] to-[#E6F0E6] py-12 px-4 relative overflow-hidden font-sans">
      {/* Vivid ambient botanical glows */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] bg-[#E89F7A] rounded-full blur-[140px] opacity-60" />
        <div className="absolute top-1/3 -left-48 w-[32rem] h-[32rem] bg-[#9BBF9B] rounded-full blur-[120px] opacity-50" />
        <div className="absolute -bottom-40 right-1/4 w-[30rem] h-[30rem] bg-[#D4A574] rounded-full blur-[140px] opacity-50" />
      </div>

      <div className="relative z-10 w-full max-w-xl animate-fade-up">
        {/* Logo & masthead */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-5xl md:text-6xl text-[#1E1B18] tracking-tight">
            Her Season
          </h1>
          <p className="mt-3 text-[11px] tracking-[0.3em] uppercase text-[#A65A3A] font-bold">
            Your Journey · Your Power
          </p>
        </div>

        {/* Main botanical card */}
        <div className="bg-[#FFFBF7] shadow-[0_28px_90px_rgba(80,50,35,0.18)] rounded-[2.5rem] overflow-hidden border border-[#E8D4C8]">
          {/* Hero botanical image — full colour */}
          <div className="h-64 w-full overflow-hidden relative">
            <img
              src={welcomeBotanical}
              alt="Soft botanical blooms in warm morning light"
              className="w-full h-full object-cover"
              width={1152}
              height={576}
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#FFFBF7] via-transparent to-transparent" />
          </div>

          <div className="p-8 md:p-12 space-y-8">
            <div className="space-y-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1E1B18] leading-tight">
                Hey lovely,
                <span className="block italic font-light text-[#A65A3A]">
                  we're so glad you're here.
                </span>
              </h2>
              <div className="space-y-4 text-[#3A3530] leading-relaxed text-base">
                <p>
                  Whether you're just starting to notice changes, you're in the
                  thick of it, or you're looking to feel more like yourself
                  again — you're in the right place.
                </p>
                <p>
                  Her Season is a quiet space for tracking what your body's
                  telling you, learning what actually helps, and finding the
                  company of women who get it.
                </p>
              </div>
            </div>

            {/* Info sections */}
            <div className="space-y-5">
              <div className="group relative pl-5">
                <div className="absolute left-0 top-1 bottom-1 w-[3px] bg-[#C45A36] rounded-full" />
                <h3 className="text-[10px] uppercase tracking-widest text-[#C45A36] font-bold mb-1">
                  What this is
                </h3>
                <p className="text-sm text-[#3A3530]">
                  A companion for reflection, tracking, and evidence-based
                  learning alongside a warm community.
                </p>
              </div>

              <div className="group relative pl-5">
                <div className="absolute left-0 top-1 bottom-1 w-[3px] bg-[#5A8A5A] rounded-full" />
                <h3 className="text-[10px] uppercase tracking-widest text-[#5A8A5A] font-bold mb-1">
                  What this isn't
                </h3>
                <p className="text-sm text-[#3A3530]">
                  A replacement for medical advice or diagnosis. Always reach
                  out to your healthcare provider for concerns.
                </p>
              </div>

              <div className="group relative pl-5">
                <div className="absolute left-0 top-1 bottom-1 w-[3px] bg-[#7A6B5E] rounded-full" />
                <h3 className="text-[10px] uppercase tracking-widest text-[#7A6B5E] font-bold mb-1">
                  Your privacy
                </h3>
                <p className="text-sm text-[#3A3530]">
                  Your data is yours. We use it only to help you notice patterns
                  and personalize your experience.
                </p>
              </div>
            </div>

            {/* Action area */}
            <div className="space-y-6 pt-2">
              <label
                htmlFor="acknowledge"
                className="flex items-start gap-4 cursor-pointer group"
              >
                <div className="mt-1 flex-shrink-0 relative flex items-center justify-center">
                  <input
                    id="acknowledge"
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="peer h-5 w-5 rounded-md border-2 border-[#D4C4B8] appearance-none checked:bg-[#C45A36] checked:border-[#C45A36] transition-all cursor-pointer"
                  />
                  <svg
                    className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={4}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span className="text-xs leading-relaxed text-[#5A4E44] group-hover:text-[#3A3530] transition-colors">
                  I understand Her Season is a wellness companion and not a
                  substitute for medical advice. I'll consult my healthcare
                  provider for medical concerns.
                </span>
              </label>

              <button
                type="button"
                onClick={handleContinue}
                disabled={!acknowledged}
                className="w-full bg-[#C45A36] hover:bg-[#A84A2B] text-white py-5 rounded-2xl font-bold tracking-[0.1em] text-sm transition-all shadow-xl shadow-[#C45A36]/35 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none"
              >
                LET'S GET STARTED
              </button>

              <p className="text-center text-sm italic text-[#7A6B5E] font-serif">
                Think of us as a knowledgeable friend... here to listen and
                walk alongside you.
              </p>
            </div>
          </div>
        </div>

        {/* Page footer */}
        <div className="mt-10 text-center">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#9A7B6A] font-bold">
            Her Season · Grace &amp; Dignity
          </p>
        </div>
      </div>
    </div>
  );
}
